import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen, ChevronLeft, Search, Map, Swords, Users, Sparkles,
  Dices, Share2, Wand2, Layers, Eye, ShieldCheck, Package, FileText,
  GitBranch, MapPin, Flag, Crown, HelpCircle, MessageCircle,
  Mic, AtSign, ScrollText, Command, FolderTree, Keyboard,
} from "lucide-react";

const GUIDES = [
  {
    id: "getting-started",
    icon: BookOpen,
    title: "Getting Started",
    summary: "Create your first campaign in under 2 minutes.",
    steps: [
      "From the Dashboard, click \u201cNew Campaign\u201d and give it a name + short description.",
      "Open the campaign. The left sidebar is grouped into World, People & Creatures, Inventory, Tabletop and Tools \u2014 every page is one click away.",
      "Add a campaign banner by clicking \u201cAdd header image\u201d at the top of the sidebar (Adventurer tier+).",
      "Create your first Story chapter \u2192 add Session pages underneath it \u2192 quick-notes during play roll up into a full chronicle.",
      "Tip: hit \u2318K (Mac) or Ctrl+K (Windows) anywhere to open Universal Search and jump to any NPC, location, lore entry, or map.",
    ],
  },
  {
    id: "parchment-sheets",
    icon: ScrollText,
    title: "Character Sheets \u2014 Parchment Layout",
    summary: "PC / NPC / Companion / Creature sheets, each with their own look.",
    steps: [
      "Click any character card to open the sheet, or hit \u201cOpen Sheet\u201d in the slide-out panel for the full parchment view.",
      "PC sheets get the full classic layout: Class / HP / AC cards on top, six ability cards (click MOD to roll, right-click SAVE to mark proficient), Skills sidebar with passive scores, and Combat / Inventory / Features tabs.",
      "NPCs use a trimmed parchment sheet \u2014 stats + skills only, no inventory clutter \u2014 so you can capture flavour fast.",
      "Companions and Summons render a modern compact stat block (cream + brown, MOD/SAVE table, Skills/Senses/CR on the right).",
      "Creatures in the Bestiary use the classic stat-block parchment scroll \u2014 dark-red name, red dividers, Actions / Bonus Actions / Reactions / Legendary Actions in a right column.",
      "Switch a PC's ruleset edition from the sheet header to flip default skill lists and proficiency bonus math.",
    ],
  },
  {
    id: "worldbuilding",
    icon: Map,
    title: "Worldbuilding \u2014 Locations, Maps & Lore",
    summary: "Build a world your players can explore turn-by-turn.",
    steps: [
      "Locations is a 3-panel World Notebook: Regions on the left, Locations of the active region in the middle, full detail (with hero image, 6 stat tabs, rich-text autosave) on the right.",
      "Buildings inside Locations carry menu / price lists \u2014 perfect for shops, taverns, and apothecaries.",
      "Upload world maps and click on them to place pins that link directly to Locations.",
      "Write Lore entries (history, religions, organisations) \u2014 the rich text editor supports headings, lists, quotes, color, highlights, and \u2192 see @mentions guide below.",
      "Toggle visibility on any Location / Map / Faction / World History entry to keep secrets from players.",
    ],
  },
  {
    id: "mentions",
    icon: AtSign,
    title: "@Mention Linking in Rich Text",
    summary: "Type @ to auto-link any entity \u2014 your worldbook becomes a wiki.",
    steps: [
      "Anywhere a rich-text editor appears (Story Notebook, Location overview, Lore entries, Deities, etc.), start typing @ followed by a name.",
      "A dropdown shows the top 8 matches across NPCs, characters, creatures, locations, lore, deities, and items \u2014 each colour-coded by type.",
      "Use \u2191 / \u2193 arrows or your mouse, press Enter / Tab or click to insert. The selected entity becomes a styled link to its sheet.",
      "Press Esc to cancel the suggestion popup mid-type.",
      "Clicking a @mention later jumps straight to that entity\u2019s page \u2014 great for cross-referencing without leaving the page.",
      "Tip: still works the old way too \u2014 click the @ icon in the editor toolbar to open the search popup manually.",
    ],
  },
  {
    id: "folders",
    icon: FolderTree,
    title: "Folder Library — Organising Everything",
    summary: "Drag-and-drop folder cards on every entity page.",
    steps: [
      "Maps, NPCs, Items, Lore, Creatures and Handouts all share the same folder system. Click \u201cNew Folder\u201d to start grouping.",
      "Folders show up as big folder cards inside the entity grid \u2014 click one to drill into its contents (breadcrumbs at the top take you back).",
      "Drag any card onto a folder card to move it; folders can nest as deep as you like.",
      "Give a folder a colour from its right-click menu \u2014 each card inside picks up a coloured stripe on its left edge so you can spot related entities instantly.",
      "Click that coloured stripe to quick-filter the grid to only that folder. Click it again to clear.",
      "The left rail lets you switch between All / each folder / Archived without losing context.",
    ],
  },
  {
    id: "npcs-characters",
    icon: Users,
    title: "Characters, NPCs & the Bestiary",
    summary: "Track every face at the table (and across the map).",
    steps: [
      "Characters: create manually or paste a character sheet URL to auto-import stats, race, class, level, and backstory.",
      "Use the new ruleset edition toggle on the character sheet to switch default skill lists + proficiency math.",
      "Each character / NPC sheet has its own Session History feed showing every session log they\u2019re mentioned in \u2014 perfect for catching up between games.",
      "NPCs: pick from 30+ professions (innkeeper, blacksmith, priest, apothecary\u2026) \u2014 they auto-link to Locations.",
      "Bestiary: ships 334 full SRD creatures with ability scores, traits, actions, legendary actions, saves, skills, resistances, and immunities, rendered in the classic parchment scroll layout.",
      "\u201cAdd to Bestiary\u201d copies a creature into your campaign so you can tweak HP or rename variants freely.",
      "Every entity supports drag-and-drop image upload and AI image generation.",
    ],
  },
  {
    id: "vtt",
    icon: Layers,
    title: "Virtual Tabletop (VTT) \u2014 Running Live Games",
    summary: "Real-time battle maps, tokens, fog of war, spells, and dice.",
    steps: [
      "Open \u201cTools \u2192 Virtual Tabletop\u201d inside any campaign, then click \u201cCreate Session\u201d.",
      "Drag a Map onto the canvas, or pick one from the Maps folder in the left sidebar.",
      "Spawn tokens by clicking Player Characters, NPCs, or Creatures in the asset tree \u2014 each token auto-imports HP, AC, size, and portrait.",
      "Paint fog of war with the brush tool; the eraser now chips away pixel-by-pixel so you can reveal exactly what the party can see.",
      "Drag a spell from the Spell Browser onto the map \u2014 a ghost preview of the AoE shape follows your cursor; scroll while dragging to rotate cones and lines before you drop.",
      "Right-click a token for the context menu: Attack animation, Mark Dead, Conditions, Hide from Players, Move to GM layer, Remove.",
      "Campaign Tools rail on the right lets you open Story, Sessions, Lore, and NPCs in a side panel without leaving the tabletop \u2014 the full campaign is always one click away.",
    ],
  },
  {
    id: "combat",
    icon: Swords,
    title: "Combat, Initiative & Dice",
    summary: "Roll-and-track the whole fight from one screen.",
    steps: [
      "Open the Combat Tracker from Tools, or start it live from the VTT top bar.",
      "Quick-add characters, NPCs, and creatures \u2014 HP, AC, and initiative auto-populate from their stat blocks.",
      "Click a combatant\u2019s name or eye icon to open the full stat block side-by-side.",
      "Damage / Heal buttons on each combatant auto-apply to HP; the tracker auto-advances turns in initiative order.",
      "The floating Dice Roller supports d4 / d6 / d8 / d10 / d12 / d20 / d100 with modifiers \u2014 rolls are synced to all connected players on the VTT.",
    ],
  },
  {
    id: "audio-recap",
    icon: Mic,
    title: "Audio Session Recording \u2192 AI Recap",
    summary: "Upload a session recording, get a structured chronicle back.",
    steps: [
      "Open Story Notebook \u2192 pick a story \u2192 click the microphone icon next to the page tree to open the Audio Ingest dialog.",
      "Drop in any audio file up to 25 MB (mp3 / m4a / wav / ogg / mp4 audio). For longer sessions, split into chunks first.",
      "Give the recording a title and (optionally) pick a chapter to attach it to. Hit Upload.",
      "We transcribe the audio, then run the transcript through our AI to produce: a 2-3 sentence recap, 5-12 key events in past tense, plus lists of characters / locations / items mentioned.",
      "The result lands as a new Session Log under your story, complete with the full transcript saved as a separate entry for fidelity.",
      "Characters that get name-dropped automatically appear in their per-character Session History feed \u2014 no extra tagging needed.",
    ],
  },
  {
    id: "power-user",
    icon: Keyboard,
    title: "Power-User Chrome \u2014 Tabs, Search, Shortcuts",
    summary: "Drive the app like a desktop notebook.",
    steps: [
      "Document Tabs (browser-style): every campaign page you open pins itself as a tab above the content area. Middle-click closes, drag to reorder, Alt+W closes the active tab, Alt+\u2190 / Alt+\u2192 cycles.",
      "Universal Search: \u2318K (Mac) or Ctrl+K (Windows) opens a command palette that searches across 8 entity collections + every campaign section. Press / to open it without a modifier (when no input is focused).",
      "Right-click any card on any grid (NPCs, Items, Maps, Lore, Creatures, Handouts, Factions, Deities, Chapters, Quests) for: Open / Open in New Tab / Rename / Move to folder \u25b8 / Pin to Sidebar / Delete.",
      "Pin any story, NPC, or location to the sidebar \u2014 it lives in the Pinned & Recent block at the bottom for quick return trips.",
      "All keyboard shortcuts respect form inputs, so / and \u2318K never hijack your typing.",
    ],
  },
  {
    id: "player-wiki",
    icon: Eye,
    title: "Player Wiki \u2014 Share What's Been Uncovered",
    summary: "A single page where your party can re-read everything you've revealed.",
    steps: [
      "Anything you mark visibility = \u201cAll Players\u201d on a Location, Map, NPC, Lore entry, Faction, Deity, Item, Handout, or Timeline event automatically appears in the Player Wiki for that campaign.",
      "Players (whether logged in or guest-invited) reach the wiki from their Player Portal \u2014 hit \u201cOpen Wiki\u201d in the top bar.",
      "The wiki has its own URL too: /campaign/<campaign-id>/wiki \u2014 share that with your party once they\u2019re invited.",
      "Left rail navigation splits the content into Lore / Towns & Places / Maps / People / Factions / Deities / Items / Handouts / Timeline. Counts next to each section show how much has been revealed.",
      "Universal search at the top searches across every revealed entry so players can find what they\u2019ve been told without nagging you.",
      "Hide content again at any time by flipping visibility back to DM Only \u2014 it vanishes from the wiki instantly.",
    ],
  },
  {
    id: "sharing",
    icon: Share2,
    title: "Inviting Players & Sharing",
    summary: "Share the campaign without your players needing an account.",
    steps: [
      "Open Party \u2192 \u201cInvite Player\u201d. A permanent link is generated.",
      "Send the link to a player \u2014 they open it on any device (no signup required), pick their character name, and land on the Player Portal.",
      "Players see: their assigned Character sheet, shared Locations / Maps / Factions / World History, shared Session recaps, and the Player VTT.",
      "As DM, use the Visibility toggle on any Location / Map / Session / Handout to reveal it to players.",
      "Sharing a Handout from the VTT instantly pops it up on every connected player\u2019s Player VTT screen.",
    ],
  },
  {
    id: "handouts-family-trees",
    icon: GitBranch,
    title: "Handouts & Family Trees",
    summary: "Share letters, maps, and secrets \u2014 and visualise relationships.",
    steps: [
      "Handouts page: create markdown handouts with rich text and images. One-click \u201cAI Create Handout\u201d generates both text and artwork.",
      "Hit \u201cShare\u201d to push a handout to players \u2014 it pops up instantly on their screen with full scrollable content.",
      "Family Trees: drag NPCs / Characters onto the canvas and draw lines between them (10 relationship types: parent, sibling, spouse, rival, mentor\u2026).",
      "Multiple trees per campaign \u2014 build one per noble house or faction.",
    ],
  },
  {
    id: "ai",
    icon: Wand2,
    title: "AI Oracle & AI Image Generation",
    summary: "Never lose momentum with a blank page.",
    steps: [
      "AI Oracle (Adventurer+): ask for NPC names, quest hooks, tavern descriptions, random encounters \u2014 results come back in seconds.",
      "Every create dialog (NPCs, Creatures, Items, Lore, Deities, Chapters, Locations) has an \u201cAI Create\u201d mode \u2014 type a prompt or drop a PDF and the fields auto-fill.",
      "Any entity can generate an image with one click \u2014 token art, item sketches, NPC portraits, handout artwork.",
      "Tokens can be regenerated from the VTT directly with the wand icon.",
    ],
  },
  {
    id: "export-subscription",
    icon: Crown,
    title: "Exports, Storage & Subscription",
    summary: "Keep a backup, upgrade when you outgrow the free tier.",
    steps: [
      "Export any campaign to PDF or DOCX from the Dashboard \u2192 campaign card menu (Adventurer+).",
      "Commoner (Free): 1 campaign, 50MB storage, core worldbuilding + combat + dice.",
      "Adventurer (\u00a320/yr): 5 campaigns, 500MB, AI Oracle, AI Audio Session Recap, AI image generation, DM Screen, full SRD Bestiary, parchment character sheets, Player Wiki, Party invites, Character URL import, Exports, VTT.",
      "Archmage (\u00a330/yr): Unlimited campaigns, 2GB storage, higher AI credits, early access, priority support, everything else included.",
      "Upgrade / manage billing from the Crown badge in the top-right header.",
    ],
  },
];

export const HelpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  // Pick up `#getting-started` style hashes on first load + subsequent nav
  const initialId = location.hash ? location.hash.replace(/^#/, "") : "getting-started";
  const [activeId, setActiveId] = useState(initialId);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    if (GUIDES.some(g => g.id === id)) setActiveId(id);
  }, [location.hash]);

  const selectGuide = (id) => {
    setActiveId(id);
    // Reflect in URL so guides become directly linkable.
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? GUIDES.filter(
        (g) =>
          g.title.toLowerCase().includes(q) ||
          g.summary.toLowerCase().includes(q) ||
          g.steps.some((s) => s.toLowerCase().includes(q))
      )
    : GUIDES;

  const activeGuide = GUIDES.find(g => g.id === activeId) || GUIDES[0];
  const ActiveIcon = activeGuide.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="help-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 flex-1">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-serif text-foreground">Help & Guides</h1>
          </div>
          {/* Search lives in the header on this layout so it's always reachable */}
          <div className="relative w-full max-w-xs hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides…"
              className="pl-9 h-9"
              data-testid="help-search"
            />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-12 gap-6">
        {/* LEFT — TOC */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3" data-testid="help-toc">
          {/* Mobile search */}
          <div className="md:hidden mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search guides…" className="pl-9 h-9" data-testid="help-search-mobile" />
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-2 px-1">All Guides</p>
          <nav className="space-y-1 md:sticky md:top-20">
            {filtered.map((g) => {
              const Icon = g.icon;
              const active = activeId === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => selectGuide(g.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left transition-colors ${
                    active
                      ? "bg-primary/15 text-primary border border-primary/30"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent"
                  }`}
                  data-testid={`help-topic-${g.id}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="flex-1 truncate text-xs sm:text-sm">{g.title}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6 px-2">
                No guides match &ldquo;{query}&rdquo;.
              </p>
            )}
          </nav>
        </aside>

        {/* RIGHT — selected guide */}
        <main className="col-span-12 md:col-span-8 lg:col-span-9" data-testid="help-content">
          <article id={`guide-${activeGuide.id}`} className="bg-card border border-border rounded-lg p-6 lg:p-8 scroll-mt-24" data-testid={`help-guide-${activeGuide.id}`}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-sm bg-primary/15 flex items-center justify-center flex-shrink-0">
                <ActiveIcon className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl text-foreground">{activeGuide.title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{activeGuide.summary}</p>
              </div>
              <button
                onClick={() => {
                  const url = `${window.location.origin}${window.location.pathname}#${activeGuide.id}`;
                  try {
                    navigator.clipboard.writeText(url);
                  } catch { /* ignore */ }
                }}
                className="text-[11px] uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors flex-shrink-0 hidden sm:block"
                title="Copy direct link"
                data-testid="help-copy-link"
              >
                Copy link
              </button>
            </div>
            <ol className="space-y-3 mt-6 border-t border-border/50 pt-5">
              {activeGuide.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-foreground/90 flex-1">{step}</span>
                </li>
              ))}
            </ol>

            {/* Next / prev navigation */}
            <div className="mt-8 pt-5 border-t border-border/50 flex items-center justify-between text-sm">
              {(() => {
                const idx = GUIDES.findIndex(g => g.id === activeId);
                const prev = idx > 0 ? GUIDES[idx - 1] : null;
                const next = idx < GUIDES.length - 1 ? GUIDES[idx + 1] : null;
                return (
                  <>
                    {prev ? (
                      <button onClick={() => selectGuide(prev.id)} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5" data-testid="help-prev-guide">
                        <ChevronLeft className="w-4 h-4" /> {prev.title}
                      </button>
                    ) : <span />}
                    {next ? (
                      <button onClick={() => selectGuide(next.id)} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 text-right" data-testid="help-next-guide">
                        {next.title} <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    ) : <span />}
                  </>
                );
              })()}
            </div>
          </article>

          {/* Support CTA */}
          <div className="mt-6 bg-card border border-border rounded-lg p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-serif text-base text-foreground mb-0.5">Still stuck?</p>
              <p className="text-xs text-muted-foreground">
                Can&apos;t find your answer? Send us a message &mdash; we usually reply within a day.
              </p>
            </div>
            <Button onClick={() => navigate("/support")} data-testid="help-contact-support" size="sm">
              Contact Support
            </Button>
          </div>

          {/* Credits & Attribution */}
          <div className="mt-6 pt-6 border-t border-border space-y-4 text-xs text-muted-foreground" data-testid="help-attribution">
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Credits & Attribution</p>
              <p className="leading-relaxed">
                NaturalOne-Chronicles is built with love for the D&D community. The following open content is used under license:
              </p>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-foreground/90 font-medium">D&D 5e SRD Creatures, Spells & Game Rules</p>
                <p>
                  Distributed under the Open Gaming License (OGL) v1.0a. The Bestiary&apos;s 334 stat blocks, spell descriptions, and core rules
                  are derived from the System Reference Document 5.1 (&copy; Wizards of the Coast LLC).
                </p>
              </div>
              <div>
                <p className="text-foreground/90 font-medium">Map Asset Library Sprites</p>
                <p>
                  The top-down (bird&apos;s-eye view) map decorations &mdash; furniture, camp gear, dungeon dressings, nature, town props, and combat fixtures &mdash;
                  were generated specifically for NaturalOne-Chronicles using OpenAI&apos;s GPT Image 1 model. All sprites are rendered from a 90&deg; overhead
                  orthographic angle for true 2D battle-map use. Generation was orchestrated via the Emergent universal LLM key.
                </p>
              </div>
              <div>
                <p className="text-foreground/90 font-medium">AI Generation</p>
                <p>
                  AI-generated NPC text, descriptions, and images are produced via the Emergent universal LLM key (OpenAI / Gemini / Claude).
                  You retain full ownership of any content created in your campaigns.
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground/70 pt-2">
                &copy; {new Date().getFullYear()} NaturalOne-Chronicles. Made by a Dungeon Master, for Dungeon Masters.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
